import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import MoneyInput from "@/components/MoneyInput";
import { ReceiptField, ReceiptInline } from "@/components/ReceiptAttach";
import { CaixaCdiCard } from "@/components/CaixaCdiCard";
import { AjusteSaldoDialog, AjustesHistory } from "@/components/CaixaAjuste";
import { CaixaExtratoButton } from "@/components/CaixaExtrato";
import { BulkAckBar } from "@/components/BulkAckBar";
import { CaixaInvestCard } from "@/components/CaixaInvestCard";
import {
  Loader2,
  ShieldCheck,
  TrendingUp,
  TrendingDown,
  CalendarDays,
  ChevronRight,
  Plus,
  Trash2,
  Wallet,
  CheckCircle2,
  Clock,
  Undo2,
} from "lucide-react";

const TODAY = new Date().toISOString().slice(0, 10);
const SAIDA_LABEL = {
  despesa_orcamento: "Gasto de orçamento",
  cartao_credito: "Fatura do cartão",
  imposto_das: "Imposto (DAS)",
  despesa_admin: "Despesa administrativa",
  retirada: "Retirada",
  outros: "Outros",
};

export default function FinanceCaixa() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const [saidaOpen, setSaidaOpen] = useState(false);
  const [ajusteOpen, setAjusteOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const load = async (m = "") => {
    setLoading(true);
    try {
      const r = await api.get("/finance/caixa", { params: m ? { month: m } : {} });
      setData(r.data || null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(month); }, [month]);

  const removeSaida = async (id) => {
    if (!window.confirm("Remover esta saída do caixa?")) return;
    try {
      await api.delete(`/finance/caixa/saidas/${id}`);
      toast.success("Saída removida");
      load(month);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remover");
    }
  };

  const ackEntry = async (id) => {
    try {
      await api.post(`/finance/caixa/ack/${id}`);
      toast.success("Repasse confirmado — valor adicionado ao caixa");
      load(month);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao confirmar");
    }
  };

  const unackEntry = async (id) => {
    if (!window.confirm("Voltar esta entrada para 'Aguardando repasse'?")) return;
    try {
      await api.delete(`/finance/caixa/ack/${id}`);
      toast.success("Entrada voltou para aguardando");
      load(month);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao desfazer");
    }
  };

  const entries = data?.entries || [];
  const pending = entries.filter((e) => e.caixa_ack !== true);
  const acked   = entries.filter((e) => e.caixa_ack === true);

  if (loading && !data) return (
    <div className="flex items-center justify-center py-16 text-slate-500">
      <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando…
    </div>
  );

  return (
    <div className="flex flex-col gap-5 max-w-5xl pb-16" data-testid="finance-caixa-page">
      <div className="flex items-center gap-3 order-[-2]">
        <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-black text-slate-900">
            Caixa da empresa
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Reserva de {data?.reserve_pct != null ? `${Number(data.reserve_pct).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%` : "—"} consolidada por orçamento, com entradas e saídas.
          </p>
        </div>
        <Button
          onClick={() => setSaidaOpen(true)}
          data-testid="btn-nova-saida"
          className="h-10 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs sm:text-sm font-black"
        >
          <TrendingDown className="w-4 h-4 mr-1" />
          Nova saída
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-gradient-to-br from-sky-500 to-sky-600 text-white p-4" data-testid="caixa-balance">
          <p className="text-[10px] font-black uppercase tracking-widest opacity-80">Saldo atual</p>
          <p className="mt-2 text-xl sm:text-2xl font-black tabular-nums">{BRL(data?.balance || 0)}</p>
          <p className="text-[10px] opacity-80 mt-0.5">Repassado − saídas{data?.total_yield ? ` + rendimentos (${BRL(data.total_yield)})` : ""}{data?.total_adjust ? ` ± ajustes` : ""}</p>
          <button onClick={() => setAjusteOpen(true)} className="mt-2 h-7 px-2.5 rounded-lg bg-white/20 hover:bg-white/30 text-[11px] font-black inline-flex items-center" data-testid="btn-ajuste-saldo">Ajuste de saldo</button>
        </div>
        <div className="rounded-2xl bg-white border border-emerald-200 p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
            <CheckCircle2 className="w-3 h-3 inline mr-0.5" /> Total recebido de notas
          </p>
          <p className="mt-2 text-xl font-black tabular-nums text-emerald-700">{BRL(data?.total_reserved || 0)}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">Soma das notas já passadas ao caixa (não é o saldo)</p>
        </div>
        <div
          className="rounded-2xl bg-white border border-amber-300 p-4"
          data-testid="caixa-pending-card"
        >
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
            <Clock className="w-3 h-3 inline mr-0.5" /> Aguardando
          </p>
          <p className="mt-2 text-xl font-black tabular-nums text-amber-700">{BRL(data?.total_pending || 0)}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            {(data?.pending_count || 0)} entrada(s) a repassar
          </p>
        </div>
        <div className="rounded-2xl bg-white border border-rose-200 p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-rose-700">
            <TrendingDown className="w-3 h-3 inline mr-0.5" /> Total saídas
          </p>
          <p className="mt-2 text-xl font-black tabular-nums text-rose-700">{BRL(data?.total_out || 0)}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">Mês: {BRL(data?.this_month_out || 0)}</p>
        </div>
      </div>

      <CaixaExtratoButton />
      <CaixaInvestCard onChanged={() => load(month)} />
      <CaixaCdiCard onChanged={() => load(month)} />
      <AjustesHistory items={data?.ajustes} onChanged={() => load(month)} />

      {/* By month */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 card-shadow">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-slate-700" />
          <h2 className="font-heading text-base font-black text-slate-900">Entradas por mês</h2>
        </div>
        {(!data?.by_month || !data.by_month.length) ? (
          <p className="text-sm text-slate-500 italic">Nenhuma reserva registrada ainda.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.by_month.map((m) => (
              <li
                key={m.month}
                className="flex items-center justify-between py-2.5 cursor-pointer hover:bg-slate-50 rounded-lg px-2"
                onClick={() => setMonth(m.month)}
                data-testid={`by-month-${m.month}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <CalendarDays className="w-4 h-4 text-sky-600 shrink-0" />
                  <div>
                    <p className="text-[13px] font-black text-slate-900">{m.month}</p>
                    <p className="text-[10.5px] text-slate-500">{m.count} orçamento(s)</p>
                  </div>
                </div>
                <p className="text-[13px] font-black tabular-nums text-sky-700">{BRL(m.total)}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Entries — split into "Aguardando repasse" (top, with CTA) and
          "No caixa" (below, confirmed).  The user wanted an explicit
          OK click per entry so they always know what's physically been
          moved to the company caixa. */}
      <section className={`bg-white rounded-2xl border p-5 card-shadow ${pending.length > 0 ? "order-[-1] border-amber-400 ring-2 ring-amber-200" : "border-amber-200"}`} data-testid="caixa-pending-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-base font-black text-amber-900 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Aguardando repasse
            {pending.length > 0 && (
              <span className="text-[10px] font-black bg-amber-200 text-amber-900 rounded-full px-2 py-0.5">
                {pending.length}
              </span>
            )}
          </h2>
          {pending.length > 1 && (
            <button
              onClick={() => setSelected(selected.length === pending.length ? [] : pending.map((e) => e.id))}
              className="text-[11px] font-black text-emerald-700 hover:underline"
              data-testid="caixa-select-all"
            >
              {selected.length === pending.length ? "Limpar seleção" : "Selecionar todos"}
            </button>
          )}
          {month && (
            <button
              onClick={() => setMonth("")}
              className="text-[11px] font-semibold text-sky-700 hover:underline"
              data-testid="caixa-clear-month"
            >
              Limpar filtro
            </button>
          )}
        </div>
        {pending.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Nada aguardando — tudo em dia 🎉</p>
        ) : (
          <ul className="divide-y divide-amber-100">
            {pending.map((e) => (
              <li
                key={e.id}
                className="py-2.5 flex items-start gap-3"
                data-testid={`caixa-entry-${e.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggleSel(e.id)}
                  aria-label="Selecionar"
                  data-testid={`caixa-select-${e.id}`}
                  className={`w-9 h-9 rounded-lg border-2 flex items-center justify-center shrink-0 transition-colors ${selected.includes(e.id) ? "bg-emerald-600 border-emerald-600 text-white" : "bg-amber-50 border-amber-300 text-amber-300"}`}
                >
                  <CheckCircle2 className="w-5 h-5" />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-black text-slate-900 leading-tight truncate">
                    Nº {e.budget_number} — {e.school_name || "—"}
                  </p>
                  <p className="text-[10.5px] text-slate-500 leading-tight">
                    NF {e.nf_number || "—"} • {formatDateBR(e.nf_date)}
                  </p>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <Link
                      to={`/orcamentos/${e.budget_id}`}
                      className="text-[10.5px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-0.5"
                    >
                      Ver orçamento <ChevronRight className="w-3 h-3" />
                    </Link>
                    <Button
                      onClick={() => ackEntry(e.id)}
                      data-testid={`caixa-ack-${e.id}`}
                      className="h-7 px-2.5 text-[11px] font-black bg-emerald-600 hover:bg-emerald-700 text-white rounded-md"
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Adicionar ao caixa
                    </Button>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[13px] font-black tabular-nums text-amber-700">{BRL(e.reserve_value)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
        <BulkAckBar pending={pending} selected={selected} setSelected={setSelected} onDone={() => load(month)} />
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-5 card-shadow" data-testid="caixa-acked-section">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-700" />
          <h2 className="font-heading text-base font-black text-slate-900">
            No caixa {month ? `• ${month}` : ""}
          </h2>
          {acked.length > 0 && (
            <span className="text-[10px] font-black bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">
              {acked.length}
            </span>
          )}
        </div>
        {acked.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Nenhum repasse confirmado ainda.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {acked.map((e) => (
              <li key={e.id} className="py-2.5 flex items-start gap-3" data-testid={`caixa-entry-${e.id}`}>
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-4 h-4 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-black text-slate-900 leading-tight truncate">
                    Nº {e.budget_number} — {e.school_name || "—"}
                  </p>
                  <p className="text-[10.5px] text-slate-500 leading-tight">
                    NF {e.nf_number || "—"} • {formatDateBR(e.nf_date)}
                    {e.caixa_ack_at && <> • <span className="text-emerald-700">repassado em {formatDateBR(e.caixa_ack_at.slice(0, 10))}</span></>}
                  </p>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <Link
                      to={`/orcamentos/${e.budget_id}`}
                      className="text-[10.5px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-0.5"
                    >
                      Ver orçamento <ChevronRight className="w-3 h-3" />
                    </Link>
                    <button
                      onClick={() => unackEntry(e.id)}
                      data-testid={`caixa-unack-${e.id}`}
                      className="text-[10.5px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-0.5"
                    >
                      <Undo2 className="w-3 h-3" /> desfazer
                    </button>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[13px] font-black tabular-nums text-emerald-700">{BRL(e.reserve_value)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Saídas */}
      <section className="bg-white rounded-2xl border border-rose-200 p-5 card-shadow">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-base font-black text-rose-900 flex items-center gap-2">
            <Wallet className="w-4 h-4" /> Saídas {month ? `• ${month}` : ""}
          </h2>
        </div>
        {(!data?.saidas || !data.saidas.length) ? (
          <p className="text-sm text-slate-500 italic">Nenhuma saída registrada.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.saidas.map((s) => (
              <li key={s.id} className="py-2.5 flex items-start gap-3" data-testid={`caixa-saida-${s.id}`}>
                <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center shrink-0">
                  <TrendingDown className="w-4 h-4 text-rose-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-black text-slate-900 leading-tight truncate">
                    {s.description || "Saída"}
                  </p>
                  <p className="text-[10.5px] text-slate-500 leading-tight">
                    {formatDateBR(s.date)} • {SAIDA_LABEL[s.category] || s.category}
                  </p>
                  <ReceiptInline kind="caixa_saida" id={s.id} path={s.receipt_path} onDone={() => load(month)} testid={`caixa-saida-receipt-${s.id}`} />
                </div>
                <div className="text-right shrink-0 flex items-center gap-2">
                  <p className="text-[13px] font-black tabular-nums text-rose-700">− {BRL(s.amount)}</p>
                  {!s.source && <button onClick={() => removeSaida(s.id)} className="text-slate-400 hover:text-rose-600" title="Remover">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <SaidaDialog open={saidaOpen} onOpenChange={setSaidaOpen} onSaved={() => { setSaidaOpen(false); load(month); }} />
      <AjusteSaldoDialog open={ajusteOpen} onOpenChange={setAjusteOpen} balance={data?.balance || 0} onSaved={() => { setAjusteOpen(false); load(month); }} />
    </div>
  );
}

function SaidaDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ date: TODAY, amount: "", description: "", category: "retirada", receipt_path: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({ date: TODAY, amount: "", description: "", category: "retirada", receipt_path: "" });
  }, [open]);

  const submit = async () => {
    const amt = parseFloat((form.amount || "").toString().replace(",", "."));
    if (!(amt > 0)) { toast.error("Informe um valor válido"); return; }
    if (!form.description.trim()) { toast.error("Descreva a saída"); return; }
    setSaving(true);
    try {
      await api.post("/finance/caixa/saidas", {
        date: form.date,
        amount: amt,
        description: form.description,
        category: form.category,
        receipt_path: form.receipt_path,
      });
      toast.success("Saída registrada");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="saida-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-rose-700">Nova saída do caixa</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Data</label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Valor</label>
            <MoneyInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} className="w-full" data-testid="saida-amount" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Categoria</label>
            <select
              className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              <option value="retirada">Retirada de sócios</option>
              <option value="despesa_admin">Despesa administrativa</option>
              <option value="outros">Outros</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Descrição</label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descreva a saída..." rows={2} />
          </div>
          <ReceiptField value={form.receipt_path} onChange={(p) => setForm((f) => ({ ...f, receipt_path: p }))} testid="saida-receipt" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="saida-submit"
            className="bg-rose-600 hover:bg-rose-700 text-white"
          >
            {saving ? "Salvando..." : "Registrar saída"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
